// Listing Groups — judgment row, Account Structure section (Standard Shopping).
// LLM-drafted verdict on whether product-partition structure funnels products coherently. This
// renderer supplies the per-ad-group structure table Claude saw, for verification.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface AdGroupRow {
  campaignName: string;
  adGroupName: string;
  unitCount: number;
  unsubdivided: boolean;
  dimensions: string[];
  everythingElseExcluded: boolean;
}

interface ListingGroupsShape {
  totalAdGroups: number;
  unsubdividedCount: number;
  everythingElseExcludedCount: number;
  adGroups: AdGroupRow[];
}

export const ListingGroupsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as ListingGroupsShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Listing-group structure
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAdGroups} Shopping ad group{e.totalAdGroups === 1 ? '' : 's'}
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalAdGroups.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Ad groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.unsubdividedCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Unsubdivided</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.everythingElseExcludedCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>"Everything else" excluded</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Per ad group</h4>
        <table className='evidence__table evidence__table--listing-groups'>
          <thead>
            <tr>
              <th>Campaign › Ad group</th>
              <th>Structure</th>
            </tr>
          </thead>
          <tbody>
            {e.adGroups.map(a => (
              <tr
                key={`${a.campaignName}-${a.adGroupName}`}
                className={classNames({ 'evidence__row--flagged': a.unsubdivided })}
              >
                <td>
                  {a.campaignName} <span className='evidence__inline-note'>› {a.adGroupName}</span>
                </td>
                <td>
                  {a.unsubdivided ? (
                    <span className='evidence__strength evidence__strength--average'>
                      Unsubdivided (all products, 1 unit)
                    </span>
                  ) : (
                    <span>
                      Subdivided by {a.dimensions.join(', ') || '(unknown)'} · {a.unitCount} units
                      {a.everythingElseExcluded && (
                        <span className='evidence__inline-note'> · "everything else" excluded</span>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Listing groups subdivide products (by type, brand, or custom label) for granular bid
        control. A single unsubdivided bucket is fine for a low-priority catch-all but a missed
        opportunity for a core campaign; an excluded "everything else" node focuses an ad group on
        specific products (intentional for themed campaigns).
      </p>
    </div>
  );
};
